
import Date from '@/components/barcodeSearch/customField/Date.vue'
import Datetime from '@/components/barcodeSearch/customField/Datetime.vue'
import Input from '@/components/barcodeSearch/customField/Input.vue'
import MultiSelect from '@/components/barcodeSearch/customField/MultiSelect.vue'
import NumberComponent from '@/components/barcodeSearch/customField/Number.vue'
import SingleSelect from '@/components/barcodeSearch/customField/SingleSelect.vue'
import SearchItemField from '@/components/barcodeSearch/SearchItemField.vue'
import InputOnlyNumber from '@/components/common/InputOnlyNumber.vue'
import SelectAutocompleteLabel from '@/components/common/SelectAutocompleteLabel.vue'
import SelectMultiAutocompleteLabel from '@/components/common/SelectMultiAutocompleteLabel.vue'
import { BARCODE_SEARCH_KEYS, CUSTOM_FIELD_KEY } from '@/utils/constants'
import { isEmpty } from '@/utils/helpers'
import { IFieldOption, TArrProjectCustomField } from '@/utils/types'
import { Options } from 'vue-class-component'
import { InjectReactive, Mixins, Prop, Watch } from 'vue-property-decorator'

export interface ICustomComponent {
  number: IComponentSetting
  text: IComponentSetting
  date: IComponentSetting
  dateTimeData: IComponentSetting
  singleSelect: IComponentSetting
  multiSelect: IComponentSetting
  email: IComponentSetting
  phoneNumber: IComponentSetting
  adjustment: IComponentSetting
}

export interface IComponentSetting {
  default: string | number | string[]
  type: string
}

@Options({
  components: {
    InputOnlyNumber,
    SelectAutocompleteLabel,
    SelectMultiAutocompleteLabel,
    Input,
    Number,
    MultiSelect,
    SingleSelect,
    Date,
    Datetime,
    NumberComponent,
  },
  name: 'SearchItemCustomField',
})
export default class SearchItemCustomField extends Mixins(SearchItemField) {
  @InjectReactive() projectVersion!: number
  @Prop({ type: Array }) readonly projectCustomFields?: TArrProjectCustomField[]

  customFieldKeySelected = ''
  isValueInOptions = false
  isChangeFromCreated = false

  get isShowComponentValue() {
    return (
      (this.projectVersion === 0 && this._searchItem?.componentType && this._searchItem?.componentType !== '') ||
      (this.isValueInOptions
        ? this.customFieldKeySelected !== ''
        : this.customFieldKeySelected !== '' &&
          this._searchItem?.componentType &&
          this._searchItem?.componentType !== '')
    )
  }

  get customFieldOptions() {
    const customFields = (this.projectCustomFields || []).reduce(
      (result: IFieldOption[], item: TArrProjectCustomField) => [
        ...result,
        ...[{ label: item.label, value: `${item.bcTypeKey}.${item.customFieldKey}` }],
      ],
      [] as IFieldOption[]
    )
    return customFields
  }

  get selectOptions() {
    const result = [] as IFieldOption[]
    for (const key in this.customFieldSettingSelected?.selections) {
      result.push({
        value: key,
        label: this.customFieldSettingSelected.selections?.[key]?.label,
      })
    }
    return result
  }

  get customFieldSelectDatas() {
    return [
      CUSTOM_FIELD_KEY.text,
      CUSTOM_FIELD_KEY.number,
      CUSTOM_FIELD_KEY.adjustment,
      CUSTOM_FIELD_KEY.singleSelect,
      CUSTOM_FIELD_KEY.multiSelect,
      CUSTOM_FIELD_KEY.date,
      CUSTOM_FIELD_KEY.dateTimeData,
      CUSTOM_FIELD_KEY.email,
      CUSTOM_FIELD_KEY.phoneNumber,
      CUSTOM_FIELD_KEY.listString,
    ]
  }

  get currentValue() {
    return this.customFieldTypeDefaultValueMap[this.customComponentFieldType].default
  }

  get currentValueType() {
    return this.customFieldTypeDefaultValueMap[this.customComponentFieldType].type
  }

  get customFieldTypeDefaultValueMap(): ICustomComponent {
    return {
      number: { default: 0, type: 'integer' },
      text: { default: '', type: 'string' },
      date: { default: [], type: 'date' },
      dateTimeData: { default: [], type: 'dateTimeData' },
      singleSelect: { default: '', type: 'string' },
      multiSelect: { default: [] as string[], type: 'string' },
      email: { default: '', type: 'string' },
      phoneNumber: { default: '', type: 'string' },
      adjustment: { default: 0, type: 'integer' },
    }
  }

  get customComponentFieldType() {
    const type = this._searchItem.componentType || 'text'
    return type as 'number' | 'text' | 'singleSelect' | 'multiSelect' | 'date' | 'email' | 'phoneNumber' | 'adjustment'
  }

  get isShowComponentType() {
    return this.projectVersion === 0 || (this.customFieldKeySelected !== '' && !this.isValueInOptions)
  }

  get customFieldMultiKey() {
    return CUSTOM_FIELD_KEY.multiSelect
  }

  getValueTypeByKey(key: keyof ICustomComponent) {
    return this.customFieldTypeDefaultValueMap[key].type
  }

  getCustomFieldComponent() {
    let component = ''
    switch (this.customComponentFieldType) {
      case CUSTOM_FIELD_KEY.text:
      case CUSTOM_FIELD_KEY.email:
      case CUSTOM_FIELD_KEY.phoneNumber:
      case CUSTOM_FIELD_KEY.ListString:
        component = 'Input'
        break
      case CUSTOM_FIELD_KEY.number:
      case CUSTOM_FIELD_KEY.adjustment:
        component = 'NumberComponent'
        this._searchItem.value = Number(this._searchItem.value)
        break
      case CUSTOM_FIELD_KEY.singleSelect:
        component = 'SingleSelect'
        break
      case CUSTOM_FIELD_KEY.date:
        component = 'Date'
        break
      case CUSTOM_FIELD_KEY.dateTimeData:
        component = 'Datetime'
        break
    }
    return component
  }

  created() {
    if (this._searchItem.subKey) {
      this.isChangeFromCreated = true
    }
    this.customFieldKeySelected = `${this._searchItem.bcTypeKey ? `${this._searchItem.bcTypeKey}.` : ''}${this
      ._searchItem.subKey ?? ''}`
  }

  updateValueInOptions(value: boolean) {
    this.isValueInOptions = value
  }

  get customFieldSettingSelected() {
    const keys = this.customFieldKeySelected.split('.')
    let customFieldKey = keys[0]
    let bcTypeKey = ''
    if (keys.length === 2) {
      customFieldKey = keys[1]
      bcTypeKey = keys[0]
    }
    return (
      (this.projectCustomFields ?? []).find(
        (item: TArrProjectCustomField) => item.customFieldKey === customFieldKey && item.bcTypeKey === bcTypeKey
      ) ?? ({} as TArrProjectCustomField)
    )
  }

  @Watch('customFieldKeySelected')
  customFieldSelectedChanged() {
    const keys = this.customFieldKeySelected.split('.')
    let customFieldKey = keys[0]
    let bcTypeKey = ''
    if (keys.length === 2) {
      customFieldKey = keys[1]
      bcTypeKey = keys[0]
    }

    this.isValueInOptions = !isEmpty(this.customFieldSettingSelected)

    if (this.isChangeFromCreated) {
      this.isChangeFromCreated = false
      if (this._searchItem.componentType === '') {
        this._searchItem.componentType = this.customFieldSettingSelected.fieldType
      }
      if (!isEmpty(this._searchItem.bcTypeKey)) {
        this._searchItem.bcTypeKey = bcTypeKey
      }
    } else {
      this._searchItem.subKey = customFieldKey
      if (this.isValueInOptions) {
        this._searchItem.componentType = this.customFieldSettingSelected.fieldType
      } else {
        this._searchItem.componentType = ''
      }
      this._searchItem.bcTypeKey = bcTypeKey
      this._searchItem.valueType = this.currentValueType
      this._searchItem.value = this.currentValue
      this._searchItem.valueList = []
    }
  }

  changeComponentType() {
    if (this._searchItem.key === BARCODE_SEARCH_KEYS.customField) {
      this._searchItem.valueType = this.currentValueType
      this._searchItem.value = this.currentValue
      this._searchItem.isExactValue = false
    }
  }
}
